import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FiHome, FiUsers, FiSettings, FiFolder, FiShield,
  FiGlobe, FiMapPin, FiMap, FiChevronDown, FiChevronRight,
  FiTag, FiPackage, FiMessageSquare, FiShoppingCart, FiImage
} from 'react-icons/fi';
import { FaShoppingCart, FaBoxOpen } from 'react-icons/fa';
import { motion } from 'framer-motion';
import '../../assets/css/layouts/Sidebar.css';

function Sidebar() {
  const location = useLocation();
  const [isGeneralMasterOpen, setIsGeneralMasterOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [userInfo, setUserInfo] = useState({ email: '', role: '', permissions: [] });

  useEffect(() => {
    const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
    setUserInfo({
      email: localStorage.getItem('email') || 'email@example.com',
      role: JSON.parse(localStorage.getItem('role')) || 'User',
      permissions,
    });

    // Open menus based on current path
    if (['/country', '/state', '/city', '/variant-name', '/variant-values', '/role'].includes(location.pathname)) {
      setIsGeneralMasterOpen(true);
      setIsCategoryOpen(false);
    } else if (['/category'].includes(location.pathname)) {
      setIsCategoryOpen(true);
      setIsGeneralMasterOpen(false);
    } else {
      setIsGeneralMasterOpen(false);
      setIsCategoryOpen(false);
    }
  }, [location.pathname]);

  const toggleGeneralMaster = () => {
    setIsGeneralMasterOpen(prev => !prev);
    setIsCategoryOpen(false); // Close other menu
  };

  const toggleCategoryMenu = () => {
    setIsCategoryOpen(prev => !prev);
    setIsGeneralMasterOpen(false); // Close other menu
  };

  const getInitial = (email) => (email ? email.charAt(0).toUpperCase() : '?');
  const { permissions } = userInfo;

  const canViewDashboard = permissions.includes("view_Dashboard");
  const canViewUsers = permissions.includes("read_User");
  const canViewRoles = permissions.includes("read_RoleAccessControl");
  const canViewSettings = permissions.includes("read_CompanySettings");
  const canViewCountry = permissions.includes("read_Country");
  const canViewState = permissions.includes("read_State");
  const canViewCity = permissions.includes("read_City");
  const canViewCategory = permissions.includes("read_Category");
  const canVariant = permissions.includes("read_VariantName");
  const canVariantValue = permissions.includes("read_VariantValue");
  const canViewProduct = permissions.includes("read_Product");
  const canViewMsg = permissions.includes("read_Message");
  const canViewOrder = permissions.includes("read_Order");
  const canViewBanner = permissions.includes("read_Banner");

  return (
    <div className="custom-sidebar d-flex flex-column text-white p-3">
      <div className="text-center mb-4 logo-section">
        <div className="profile-circle">{getInitial(userInfo.email)}</div>
        <div className="user-details mt-2">
          <div className="user-email">{userInfo.email}</div>
          <div className="user-role">Role: {userInfo.role.name}</div>
        </div>
      </div>

      <ul className="nav nav-pills flex-column sidebar-nav">
        {canViewDashboard && (
          <motion.li className="nav-item" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}>
            <Link className={`nav-link ${location.pathname === '/dashboard' ? 'active' : ''}`} to="/dashboard">
              <FiHome className="me-2" /> Dashboard
            </Link>
          </motion.li>
        )}

        {canViewCategory && (
          <motion.li className="nav-item" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}>
            <div
              className={`nav-link cursor-pointer d-flex justify-content-between ${isCategoryOpen ? 'active' : ''}`}
              onClick={toggleCategoryMenu}
            >
              <div><FaShoppingCart className="me-2" /> Category</div>
              <span>{isCategoryOpen ? <FiChevronDown /> : <FiChevronRight />}</span>
            </div>
            {isCategoryOpen && (
              <ul className="nav flex-column ms-3">
                <li className="nav-item">
                  <Link className={`nav-link ${location.pathname === '/category' ? 'active' : ''}`} to="/category">
                    <FaShoppingCart className="me-2" /> Main-Category
                  </Link>
                </li>
              </ul>
            )}
          </motion.li>
        )}

        {(canViewCountry || canViewState || canViewCity || canViewRoles || canVariant || canVariantValue) && (
          <motion.li className="nav-item" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}>
            <div
              className={`nav-link cursor-pointer d-flex justify-content-between ${isGeneralMasterOpen ? 'active' : ''}`}
              onClick={toggleGeneralMaster}
            >
              <div><FiFolder className="me-2" /> General Master</div>
              <span>{isGeneralMasterOpen ? <FiChevronDown /> : <FiChevronRight />}</span>
            </div>
            {isGeneralMasterOpen && (
              <ul className="nav flex-column ms-3">
                {canViewCountry && (
                  <li className="nav-item">
                    <Link className={`nav-link ${location.pathname === '/country' ? 'active' : ''}`} to="/country">
                      <FiGlobe className="me-2" /> Country
                    </Link>
                  </li>
                )}
                {canViewState && (
                  <li className="nav-item">
                    <Link className={`nav-link ${location.pathname === '/state' ? 'active' : ''}`} to="/state">
                      <FiMapPin className="me-2" /> State
                    </Link>
                  </li>
                )}
                {canViewCity && (
                  <li className="nav-item">
                    <Link className={`nav-link ${location.pathname === '/city' ? 'active' : ''}`} to="/city">
                      <FiMap className="me-2" /> City
                    </Link>
                  </li>
                )}
                {canVariant && (
                  <li className="nav-item">
                    <Link className={`nav-link ${location.pathname === '/variant-name' ? 'active' : ''}`} to="/variant-name">
                      <FiTag className="me-2" /> Variant Name
                    </Link>
                  </li>
                )}
                {canVariantValue && (
                  <li className="nav-item">
                    <Link className={`nav-link ${location.pathname === '/variant-values' ? 'active' : ''}`} to="/variant-values">
                      <FiPackage className="me-2" /> Variant Value
                    </Link>
                  </li>
                )}
                {canViewRoles && (
                  <li className="nav-item">
                    <Link className={`nav-link ${location.pathname === '/role' ? 'active' : ''}`} to="/role">
                      <FiShield className="me-2" /> Role & Permissions
                    </Link>
                  </li>
                )}
              </ul>
            )}
          </motion.li>
        )}

        {canViewProduct && (
          <motion.li className="nav-item" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}>
            <Link className={`nav-link ${location.pathname === '/Product' ? 'active' : ''}`} to="/Product">
              <FaBoxOpen className="me-2" /> Product
            </Link>
          </motion.li>
        )}

        {canViewUsers && (
          <motion.li className="nav-item" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}>
            <Link className={`nav-link ${location.pathname === '/users' ? 'active' : ''}`} to="/users">
              <FiUsers className="me-2" /> Users
            </Link>
          </motion.li>
        )}
        {canViewOrder && (
          <motion.li className="nav-item" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}>
            <Link className={`nav-link ${location.pathname === '/Order' ? 'active' : ''}`} to="/Order">
              <FiShoppingCart className="me-2" /> Order
            </Link>
          </motion.li>
        )}

        {canViewMsg && (
          <motion.li className="nav-item" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}>
            <Link className={`nav-link ${location.pathname === '/Message' ? 'active' : ''}`} to="/Message">
              <FiMessageSquare className="me-2" /> Message
            </Link>
          </motion.li>
        )}

        {canViewBanner && (
          <motion.li className="nav-item" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}>
            <Link className={`nav-link ${location.pathname === '/Banner' ? 'active' : ''}`} to="/Banner">
              <FiImage className="me-2" /> Banner
            </Link>
          </motion.li>
        )}

        {canViewSettings && (
          <motion.li className="nav-item" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}>
            <Link className={`nav-link ${location.pathname === '/settings' ? 'active' : ''}`} to="/settings">
              <FiSettings className="me-2" /> Company Settings
            </Link>
          </motion.li>
        )}
      </ul>
    </div>
  );
}

export default Sidebar;
